import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { createSelector } from 'reselect';

import { Loader } from '@opendoor/bricks';
import * as sessionActions from '@opendoor/shared-fe/build/store/session/actions';
import * as sessionSelectors from '@opendoor/shared-fe/build/store/session/selectors';
import { trackEvent } from '../../api/analytics';
import LoginOrRegisterBox from '../../components/account/LoginOrRegisterBox';
import { IRequestStatus } from '../../declarations';
import parseQueryString from '../../helpers/parseQueryString';
import layoutWrapper from '../../hocs/layoutWrapper';

export interface IQueryParams {
  config?: 'resaleClosingDashboard';
  email?: string;
  name?: string;
  phone?: string;
  redirectTo?: string;
}

export interface IRegistrationPageProps extends RouteComponentProps<{}> {
  loading?: boolean;
  loggedIn: boolean;
  logIn: (email: string, password: string, rememberMe: boolean) => void;
  loginRequestStatus: IRequestStatus;
  register: (
    email: string,
    name: string,
    password: string,
    phone: string,
    rememberMe: boolean,
  ) => void;
  registrationRequestStatus: IRequestStatus;
  serverLoginRedirect: string | undefined;
  sessionInfoIsLoading: boolean;
}

export class RegistrationPage extends React.PureComponent<IRegistrationPageProps> {
  public componentDidMount() {
    trackEvent('registration', 'load', undefined, this.getQueryParams());
    this.redirectIfLoggedIn();
  }

  public componentWillReceiveProps(nextProps: IRegistrationPageProps) {
    this.redirectIfLoggedIn(nextProps);
  }

  public redirectIfLoggedIn(nextProps?: IRegistrationPageProps) {
    const { loggedIn, history, serverLoginRedirect } = nextProps || this.props;
    if (loggedIn) {
      const queryParams = this.getQueryParams();
      // TODO: Maybe support window.location.assign with white-listed domains for
      // URLs or paths not in the router.
      trackEvent('registration', 'redirect', undefined, {
        ...queryParams,
        serverLoginRedirect,
      });
      if (!queryParams.redirectTo && serverLoginRedirect && serverLoginRedirect !== '/') {
        window.location.assign(serverLoginRedirect);
      } else {
        history.push(queryParams.redirectTo || '/');
      }
    }
  }

  public getQueryParams(): IQueryParams {
    const { location } = this.props;
    return parseQueryString(location.search);
  }

  public render(): JSX.Element {
    const {
      logIn,
      loginRequestStatus,
      register,
      registrationRequestStatus,
      sessionInfoIsLoading,
    } = this.props;
    const { config, email, name, phone } = this.getQueryParams();

    if (sessionInfoIsLoading) {
      return <Loader />;
    }

    let loginHeaderText: string | undefined;
    let registrationHeaderText: string | undefined;
    switch (config) {
      case 'resaleClosingDashboard':
        loginHeaderText = 'Sign in to access your dashboard';
        registrationHeaderText = 'Sign up to get access to your dashboard';
        break;
      default:
        break;
    }

    // TODO: Fetch email already registered info
    return (
      <div className="registration-page">
        <LoginOrRegisterBox
          email={email}
          loginHeaderText={loginHeaderText}
          loginRequestStatus={loginRequestStatus}
          name={name && name.replace(/\+/g, ' ')}
          phone={phone}
          onLoginSubmit={logIn}
          onRegistrationSubmit={register}
          registrationHeaderText={registrationHeaderText}
          registrationRequestStatus={registrationRequestStatus}
        />
      </div>
    );
  }
}

const mapStateToProps = createSelector(
  [
    sessionSelectors.loggedIn,
    sessionSelectors.loginRequestStatus,
    sessionSelectors.infoIsLoading,
    sessionSelectors.registrationRequestStatus,
    sessionSelectors.serverLoginRedirect,
  ],
  (
    loggedIn: boolean,
    loginRequestStatus: IRequestStatus,
    sessionInfoIsLoading: boolean,
    registrationRequestStatus: IRequestStatus,
    serverLoginRedirect: string | undefined,
  ) => ({
    loggedIn,
    loginRequestStatus,
    registrationRequestStatus,
    serverLoginRedirect,
    sessionInfoIsLoading,
  }),
);

const mapDispatchToProps = {
  logIn: sessionActions.logIn,
  register: sessionActions.register,
};

export default layoutWrapper(
  withRouter(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(RegistrationPage),
  ),
  { footerType: 'NONE' },
);
